@use '../../styles/mixins.scss' as mixins;
@use '../../styles/themed' as themed;
@use '../../styles/border' as border;
@use '../../styles/typography.scss' as typography;

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition:
    background-color 100ms ease-out,
    color 100ms ease-out,
    transform 100ms ease-out;
  white-space: nowrap;
  min-width: 72px;

  &:hover:not(:disabled) {
    transition:
      background-color 150ms ease,
      color 150ms ease;
  }

  &:disabled {
    @include mixins.disabled(0.3);
    pointer-events: none;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:focus-visible {
    @include mixins.focus-ring;
  }

  &.pill {
    border-radius: 999px;
  }
}

// Sizes
.xs {
  height: 24px;
  padding: 0 6px;
  border-radius: 6px;
  gap: 0px;
  min-width: 64px;

  .label {
    @include typography.text-s-medium;
  }
}

.sm {
  height: 28px;
  padding: 0 8px;
  gap: 2px;

  .label {
    @include typography.text-s-medium;
  }
}

.md {
  height: 32px;
  padding: 0 8px;
  gap: 2px;
}

.lg {
  height: 36px;
  padding: 0 10px;
  gap: 4px;
  min-width: 76px;
}

// Variants
.primary {
  background-color: themed.color(button-accent-default);
  color: themed.color(text-on-accent);

  &:hover:not(:disabled) {
    background-color: themed.color(button-accent-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-accent-active);
  }
}

.secondary {
  background-color: themed.color(button-secondary-default);
  color: themed.color(text-primary);

  &:hover:not(:disabled) {
    background-color: themed.color(button-secondary-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-secondary-active);
  }

  &:disabled {
    background-color: themed.color(button-secondary-default);
  }
}

.secondary-dark {
  background-color: themed.color(button-secondary-dark-default);
  color: themed.color(text-primary);

  &:hover:not(:disabled) {
    background-color: themed.color(button-secondary-dark-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-secondary-dark-active);
  }

  &:disabled {
    background-color: themed.color(button-secondary-dark-default);
  }
}

.tertiary {
  background-color: themed.color(button-tertiary-default);
  color: themed.color(text-inverse);

  &:hover:not(:disabled) {
    background-color: themed.color(button-tertiary-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-tertiary-active);
  }
}

.secondary-accent {
  background-color: themed.color(button-accent-secondary-default);
  color: themed.color(text-accent);

  &:hover:not(:disabled) {
    background-color: themed.color(button-accent-secondary-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-accent-secondary-active);
  }

  &:disabled {
    background-color: themed.color(button-accent-secondary-default);
  }
}

.tertiary-accent {
  background-color: themed.color(button-accent-tertiary-default);
  color: themed.color(text-accent);
  border: border.$border-width solid themed.color(border-default);

  &:hover:not(:disabled) {
    background-color: themed.color(button-accent-tertiary-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-accent-tertiary-active);
  }

  &:disabled {
    background-color: themed.color(button-accent-tertiary-default);
  }
}

.negative {
  background-color: themed.color(button-destructive-default);
  color: themed.color(text-on-accent);

  &:hover:not(:disabled) {
    background-color: themed.color(button-destructive-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-destructive-active);
  }
}

.clear {
  background-color: transparent;
  color: themed.color(text-primary);
  border: border.$border-width solid transparent;

  &:hover:not(:disabled) {
    background-color: themed.color(button-clear-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-clear-active);
  }
}

.full {
  background-color: themed.color(button-full-default);
  color: themed.color(text-primary);
  border: border.$border-width solid themed.color(border-default);

  &:hover:not(:disabled) {
    background-color: themed.color(button-full-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-full-active);
  }
}

.outline {
  background-color: transparent;
  color: themed.color(text-primary);
  border: border.$border-width solid themed.color(border-strong);

  &:hover:not(:disabled) {
    background-color: themed.color(button-clear-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-clear-active);
  }
}

.dash {
  background-color: transparent;
  color: themed.color(text-primary);
  border: border.$border-width dashed themed.color(border-stronger);

  &:hover:not(:disabled) {
    background-color: themed.color(button-clear-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-clear-active);
  }
}

.dash {
  background-color: transparent;
  color: themed.color(text-primary);
  border: 1px dashed themed.color(border-stronger);

  &:hover:not(:disabled) {
    background-color: themed.color(button-clear-hover);
  }

  &:active:not(:disabled) {
    background-color: themed.color(button-clear-active);
  }
}

.adornment {
  display: inline-flex;
  align-items: center;
  flex-shrink: 1;
}

.label {
  @include typography.text-m-medium;
  display: inline-flex;
  align-items: center;
  padding: 0 4px;
}
